import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ExternalLink } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from "recharts";
import { tooltipStyle } from "./oversiktConstants";
import { num } from "@/lib/formatters";

const COLORS = ["#103766", "#3F8A86", "#E8A34A", "#D35E4E", "#8B5CF6"];

const fmt = (v, unit, decimals) => (v == null ? "—" : `${v.toLocaleString("nb-NO", { maximumFractionDigits: decimals ?? (unit ? 1 : 2) })}${unit ? " %" : ""}`);

export const RangeringCompareChart = ({ data }) => {
  const entries = Object.entries(data?.kommuner || {});
  if (entries.length === 0) return null;
  const metrics = (data.metrics || []).filter((m) => entries.some(([, k]) => k.metrics[m.key]?.rank));
  if (metrics.length === 0) return null;
  const year = entries.map(([, k]) => Object.values(k.metrics)[0]?.year).find(Boolean);
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="compare-rangering-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#103766]" />
          Plassering i eige fylke {year ? `(${year})` : ""}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kor kommunane rangerer blant kommunane i sitt eige fylke. Fordi fylka har ulikt tal kommunar, viser søylene plasseringa som toppandel – kort søyle = høg plassering.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {metrics.map((m) => {
          const rows = entries
            .filter(([, k]) => k.metrics[m.key]?.rank)
            .map(([code, k], i) => {
              const { rank, count, value } = k.metrics[m.key];
              return {
                code,
                name: k.name,
                "Topp %": Math.round((rank / count) * 100),
                _rank: rank,
                _count: count,
                _value: value,
                _fylke: k.fylke_name,
                _label: `${rank}. av ${count}`,
                _color: COLORS[i % COLORS.length],
              };
            });
          return (
            <div key={m.key} data-testid={`compare-rangering-${m.key}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">{m.label}</p>
              <div className="h-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 40 }} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis type="number" domain={[0, 100]} reversed stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => `${num(v)} %`} />
                    <YAxis type="category" dataKey="name" width={110} stroke="#8AA4BF" fontSize={11} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v, _n, p) => [`nr. ${p.payload._rank} av ${p.payload._count} i ${p.payload._fylke} (topp ${num(v)} %) · ${fmt(p.payload._value, m.unit, m.decimals)}`, "Plassering"]}
                    />
                    <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Topp %" radius={[0, 4, 4, 0]} barSize={18} stroke="#FFFFFF" strokeWidth={1}>
                      <LabelList dataKey="_label" position="right" fontSize={11} fill="#4B647F" />
                      {rows.map((r) => (
                        <Cell key={r.code} fill={r._color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#4B647F] mt-1">
                {rows.map((r) => (
                  <span key={r.code} data-testid={`compare-rangering-${m.key}-${r.code}`}>
                    <span className="font-medium text-[#1F2A36]">{r.name}</span>: {fmt(r._value, m.unit, m.decimals)} · nr. {r._rank} av {r._count} i {r._fylke}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
        <p className="text-xs text-[#8AA4BF]">
          Plasseringa gjeld siste publiserte rekneskapsår i kvar kommune sitt eige fylke. Kommunar som åleine utgjer eit fylke (Oslo) får ikkje fylkesplassering.{" "}
          Kjelde: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13545 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          og 12858.
        </p>
      </CardContent>
    </Card>
  );
};

export default RangeringCompareChart;
